/**
 * SiteConfigContext: React context for site configuration.
 *
 * The context value is provided by ConfigProvider (unified config system).
 * ConfigProvider loads from the new config.* tables and exposes the
 * matching ConfigResolver directly. This keeps React consumers on the
 * unified config system without fabricating a legacy row-shaped config object.
 */

import { createContext, useContext } from 'react';
import type { DeploymentTarget } from '../config/deployment';
import type { ConfigResolver } from '../config/unified/ConfigResolver';
import type { InboundXService } from '../services/inboundx';

// ============================================================================
// TYPES
// ============================================================================

/**
 * Error type classification for FATAL_CONFIG_ERROR
 * - domain_disabled: Client/config disabled for this domain (expected state, already tracked in Sentry)
 * - domain_not_configured: Domain not found in database (expected state, already tracked in Sentry)
 * - config_load_failed: Actual error during config loading (needs Sentry error logging)
 */
type ConfigErrorType = 'domain_disabled' | 'domain_not_configured' | 'config_load_failed';

export interface SiteConfigContextValue {
    currentDomain: string | null;
    currentResolver: ConfigResolver | null;
    isLoaded: boolean;
    isLoading: boolean;
    error: string | null;
    errorType?: ConfigErrorType;
    // Deployment configuration
    deploymentTarget?: DeploymentTarget;
    // API version information
    apiVersion?: string;
    environment?: string;
    apiVersionError?: string;
    // InboundXService instance (created once during initialization)
    inboundXService: InboundXService | null;
    // Core access functions
    getResolver: (domain: string) => ConfigResolver | undefined;
    isSiteSupported: (domain: string) => boolean;
    // URL restriction check
    isCurrentURLAllowed: (domain: string) => boolean;
}

// ============================================================================
// CONTEXT
// ============================================================================

const SiteConfigContext = createContext<SiteConfigContextValue | undefined>(undefined);

// ============================================================================
// HOOK
// ============================================================================

export const useSiteConfig = (): SiteConfigContextValue => {
    const context = useContext(SiteConfigContext);
    if (!context) {
        throw new Error('useSiteConfig must be used within a ConfigProvider');
    }
    return context;
};

export default SiteConfigContext;
